import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Select, Button, Form, Checkbox } from 'antd';
import { useShallow } from 'zustand/shallow';
import AlertBox from '@/components/alert-box';
import { Dialog } from '@/components/dialog/Dialog';
import { useQueryParams } from '@/hooks';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { useSalaryStore } from '@/store/useSalaryStore';

// `companyId`: a page without ?company= (the company profile) passes it here.
export default function ExportDocumentsModal({ metadata, companyId }) {
  const { t } = useTranslation(['forms', 'tables', 'menus', 'messages']);
  const {
    queryParams: { company },
  } = useQueryParams();

  const [form] = Form.useForm();

  const getCompanyIdFromQueryParams = useCallback(() => {
    if (companyId) return companyId;
    return company && company !== 'all' ? company : undefined;
  }, [company, companyId]);

  const initialValues = useMemo(() => {
    return {
      employmentType: 'unlimited',
      company: getCompanyIdFromQueryParams(),
    };
  }, [getCompanyIdFromQueryParams]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const [toggle, setToggle] = useState(false);

  const { isDirty } = useSalaryStore(
    useShallow((state) => ({
      isDirty: state.isDirty,
    }))
  );

  const { data: companyOptions, isLoading: companyIsLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesDropdown],
    queryFn: async () => {
      return await API.companies.retrieveDropdownAllCompanies(null);
    },
    select: (data) => {
      const companies = data?.items || [];

      return companies?.map((company) => ({
        label: company.name,
        value: company._id,
      }));
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isSelectedCompany = Form.useWatch('company', form);

  // Whether the selected company has non-employment workers — drives visibility
  // of the "include non-employment" checkbox.
  const { data: companyEmpData } = useQuery({
    queryKey: [QUERY_KEY.employees, 'nonEmpCheck', isSelectedCompany],
    queryFn: () =>
      API.employees.retrieveAllEmployees({
        company: isSelectedCompany,
        offset: 1,
        limit: 1,
      }),
    enabled: !!isSelectedCompany,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const hasNonEmploymentWorkers = companyEmpData?.hasNonEmploymentWorkers;

  useEffect(() => {
    form.setFieldsValue({ employee: undefined });
  }, [form, isSelectedCompany]);

  const handleCancelModal = () => {
    setToggle(false);
  };

  const onReset = () => {
    form.resetFields();
    handleCancelModal();
  };

  const onFinish = async (values) => {
    try {
      const payload = {
        companyId: values.company,
        select: {},
        sorter: {},
        skip: 0,
        offset: 0,
        limit: 99999,
        includeAdditionalInfo: values.includeAdditionalInfo || false,
        includeNonEmployment: values.includeNonEmployment || false,
      };

      await API.exports.exportEmployees(payload);

      onReset();
    } catch (error) {
      console.error('Export failed', error);
    }
  };

  const dotButtonAnimation = (dotColor) => {
    return (
      <span
        className={cn(
          'block h-2 w-2 shrink-0 rounded-full',
          dotColor ? dotColor : 'bg-zinc-100'
        )}
      >
        <span
          className={cn(
            'block h-2 w-2 shrink-0 rounded-full group-hover:animate-ping',
            dotColor ? dotColor : 'bg-zinc-100'
          )}
        />
      </span>
    );
  };

  return (
    <Dialog
      form={form}
      onSubmit={onFinish}
      title={t('export', { ns: 'menus' })}
      open={toggle}
      className="!h-auto !w-[768px]"
      onCancel={handleCancelModal}
      destroyOnHidden
      triggerChildren={
        metadata?.showDot ? (
          <Button
            title={t('export', { ns: 'menus' })}
            type="default"
            className={cn(
              'btn-default group relative justify-start px-0',
              metadata?.buttonClassName
            )}
            onClick={() => setToggle(true)}
            disabled={isDirty}
          >
            {dotButtonAnimation('bg-accent')}
            <span className="mr-2">{t('export', { ns: 'menus' })}</span>
          </Button>
        ) : (
          <Button
            title={t('export', { ns: 'menus' })}
            type="default"
            className={cn('btn-primary relative', metadata?.buttonClassName)}
            icon={<DownloadOutlined className="text-[16px]" />}
            onClick={() => setToggle(true)}
            disabled={isDirty}
          >
            <span className="mr-2">{t('export', { ns: 'menus' })}</span>
          </Button>
        )
      }
    >
      <div className="-mx-4 -my-6 grid max-h-[calc(100svh_-_298px)] gap-4 overflow-y-auto px-4 py-6">
        <Form
          form={form}
          name="export-documents-form"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          className={cn('form')}
          initialValues={initialValues}
        >
          <AlertBox variant="info" className="relative p-2">
            <span>
              {t('exportEmployeesExcelActionInfo', { ns: 'messages' })}
            </span>
          </AlertBox>

          <Form.Item
            label={t('selectCompany', { ns: 'forms' })}
            name="company"
            rules={[
              {
                required: true,
                message: t('formValidation.companyRequired', {
                  ns: 'forms',
                }),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              mode="single"
              placeholder={t('selectCompany', { ns: 'forms' })}
              className="w-full"
              optionFilterProp="label"
              loading={companyIsLoading}
              options={companyOptions}
            />
          </Form.Item>

          <Form.Item name="includeAdditionalInfo" valuePropName="checked">
            <Checkbox>
              {t('exportAdditionalColumns', { ns: 'forms' })}
            </Checkbox>
          </Form.Item>

          {hasNonEmploymentWorkers && (
            <Form.Item name="includeNonEmployment" valuePropName="checked">
              <Checkbox>
                {t('includeNonEmployment', {
                  ns: 'forms',
                  defaultValue: 'Prikaži i radnike van radnog odnosa',
                })}
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </div>
    </Dialog>
  );
}
